import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaBars } from "react-icons/fa";
import CompanyDashboard from "./CompanyDashboard";
import CompanyBottomNavbar from "./CompanyBottomNavbar";

const PENDING_INSTAGRAM_OPEN_KEY = "infuber:pendingInfluencerInstagramUrl";
const LARGE_SCREEN_QUERY = "(min-width: 1024px)";
const MEDIUM_SCREEN_QUERY = "(min-width: 768px)";

const getDefaultCollapsed = () =>
  typeof window !== "undefined" && !window.matchMedia(LARGE_SCREEN_QUERY).matches;

const getIsMdUp = () =>
  typeof window !== "undefined" && window.matchMedia(MEDIUM_SCREEN_QUERY).matches;

const getPageTitle = (pathname) => {
  if (pathname.includes("brand-managers")) return "Brand Managers";
  if (pathname.includes("/brands")) return "Brands";
  if (pathname.includes("advertising")) return "Campaign Ads";
  if (pathname.includes("/campaigns")) return "Campaigns";
  if (pathname.includes("/pipelines")) return "Pipelines";
  if (pathname.includes("/affiliates")) return "Affiliates";
  if (pathname.includes("/financial")) return "Financial";
  if (pathname.includes("social-accounts")) return "Connect Social Accounts";
  if (pathname.includes("dm-automation")) return "DM Automation";
  if (pathname.includes("youtube-media-approval")) return "YouTube Media Approval";
  if (pathname.includes("instagram-media-approval")) return "Instagram Media Approval";
  if (pathname.includes("/reports")) return "Reports";
  if (pathname.includes("/settings")) return "Settings";
  if (
    pathname.includes("/overview") ||
    pathname.endsWith("/dashboard") ||
    pathname.endsWith("/dashboard/")
  ) {
    return "Dashboard";
  }
  return "Dashboard";
};

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const { loading, isAuthenticated } = useSelector((state) => state.company);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(getDefaultCollapsed);
  const [isMdUp, setIsMdUp] = useState(getIsMdUp);
  const iconOnly = isMdUp && isCollapsed;

  useEffect(() => {
    const largeMedia = window.matchMedia(LARGE_SCREEN_QUERY);
    const mediumMedia = window.matchMedia(MEDIUM_SCREEN_QUERY);
    const handleLargeChange = (event) => setIsCollapsed(!event.matches);
    const handleMediumChange = (event) => setIsMdUp(event.matches);

    largeMedia.addEventListener("change", handleLargeChange);
    mediumMedia.addEventListener("change", handleMediumChange);
    return () => {
      largeMedia.removeEventListener("change", handleLargeChange);
      mediumMedia.removeEventListener("change", handleMediumChange);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("instagramConnected") !== "true") return;

    let pendingUrl = null;
    try {
      pendingUrl = sessionStorage.getItem(PENDING_INSTAGRAM_OPEN_KEY);
      if (pendingUrl) sessionStorage.removeItem(PENDING_INSTAGRAM_OPEN_KEY);
    } catch (_) {
      // Ignore storage errors (e.g. private mode).
    }

    if (pendingUrl) {
      window.location.href = pendingUrl;
    }
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="h-[calc(100dvh-80px)] bg-gray-100 flex flex-col overflow-hidden">
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <CompanyDashboard
          isOpen={isSidebarOpen}
          setIsOpen={setIsSidebarOpen}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          iconOnly={iconOnly}
        />

        <div
          className={`flex-1 flex flex-col min-h-0 overflow-hidden transition-all duration-300 ease-in-out ${
            iconOnly ? "md:ml-[72px]" : "md:ml-[250px]"
          }`}
        >
          <div className="md:hidden fixed top-[70px] left-0 right-0 z-30 w-full bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Open sidebar"
              >
                <FaBars className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-sm md:text-lg lg:text-lg font-semibold text-gray-800">
                {getPageTitle(location.pathname)}
              </h1>
              <div className="w-10"></div>
            </div>
          </div>

          <main className="flex flex-1 min-h-0 flex-col overflow-y-auto pt-12 md:pt-0 pb-20 md:pb-6 bg-[#F4F2EF]">
            <div className="w-full border-l border-[#E3DFD9] bg-[#F4F2EF]">
              {children}
            </div>
          </main>
        </div>
      </div>

      <CompanyBottomNavbar
        isSidebarOpen={isSidebarOpen}
        onMoreClick={() => setIsSidebarOpen((prev) => !prev)}
      />
    </div>
  );
};

export default DashboardLayout;
